import { useCallback, useEffect, useRef } from 'react';
import { CategoryAccordion } from './CategoryAccordion';
import { ProductLayout } from './ProductLayout';
import {
  SubscribableProducts,
  SubscriptionProductItem,
  SubscriptionProductViewItem,
} from '../../../utils/subscriptionDetail/types';
import { Products } from '../../../api/graphql/products/types';
import { DiffState, QuantityUpdate, useProductCart } from '../../../hooks/use-product-cart';
import { ProductDescModal } from '../../ProductDescModal/ProductDescModal';
import { BoxSelectProductsModal } from '../../BoxProductModal/BoxSelectProductsModal';
import { BoxSetContentsModal } from '../../BoxProductModal/BoxSetContentsModal';
import { calcTotalSummary, isYuPack } from '../../../utils/subscriptionDetail/utils';
import { useNextDeliverySubscription } from './useNextDeliverySubscription';
import { calcCartSummary } from '../../../utils/subscriptionDetail/productCart';
import { useLatest } from '../../../hooks/use-latest';
import styles from '../../../assets/scss/SubscriptionDetail/NextDeliverySubscription/NextDeliverySubscription.module.scss';
import backBtnClass from '../../../assets/scss/SubscriptionDetail/BackBtn.module.scss';
import { buildProductRecord } from '../../../utils/subscriptionDetail/subscriptionProducts';
import iconLoading from '../../../assets/images/icon_loading_circle.gif';

export type NextDeliveryDisplayState = 'ready' | 'loading' | 'stalled';

type Props = {
  subscribedProducts: SubscriptionProductViewItem[] | null;
  subscribableProducts: SubscribableProducts | null;
  products: Products | null;
  paramCategoryId: string;
  minBorderPrice: number;
  maxBorderPrice: number;
  showConfirmMinPrice: (minPrice: number, func: () => void) => void;
  updateSubscription: (diff: DiffState, isTimeReset: boolean) => Promise<void>;
  onEditChange: React.Dispatch<React.SetStateAction<boolean>>;
  displayState: NextDeliveryDisplayState;
  onRetryLoad: () => void;
  /** 親が商品変更成功のたびに 1 ずつ増やすと、ここで diff（カートの変更）をリセットする */
  variantsSaveSuccessKey?: number;
};

export function NextDeliverySubscription({
  subscribedProducts,
  subscribableProducts,
  products,
  paramCategoryId,
  minBorderPrice,
  maxBorderPrice,
  showConfirmMinPrice,
  updateSubscription,
  onEditChange,
  displayState,
  onRetryLoad,
  variantsSaveSuccessKey = 0,
}: Props) {
  // カート
  const { diff, changeQuantity, reCalcDiff, resetDiff } = useProductCart();
  const prevVariantsSaveSuccessKey = useRef(0);
  const shouldSkipNextSetTotalPost = useRef(false);

  useEffect(() => {
    if (
      variantsSaveSuccessKey <= 0 ||
      variantsSaveSuccessKey <= prevVariantsSaveSuccessKey.current
    ) {
      return;
    }
    prevVariantsSaveSuccessKey.current = variantsSaveSuccessKey;
    // 保存成功直後は diff のリセットに伴う一時的な setTotal 再送を 1 回だけ抑止する。
    // この再送は旧小計で評価されることがあり、テーマ側で紙吹雪が誤発火する要因になる。
    shouldSkipNextSetTotalPost.current = true;
    resetDiff();
  }, [variantsSaveSuccessKey, resetDiff]);

  const {
    isOpen,
    productModal,
    handleClickSwitchAccordion,
    closeModal,
    handleClickProductLink,
    onClickBoxProductsSelect,
    onClickBoxSetContents,
  } = useNextDeliverySubscription({ subscribableProducts, products, paramCategoryId });

  useEffect(() => {
    onEditChange(diff.size !== 0);
  }, [diff, onEditChange]);

  // ====== 商品変更関連 ======
  const latest = useLatest({
    diff,
    subscribedProducts,
    minBorderPrice,
    showConfirmMinPrice,
    updateSubscription,
  });

  useEffect(() => {
    if (!subscribableProducts || !subscribedProducts) return;

    const productRec = buildProductRecord(subscribedProducts, subscribableProducts.products);
    reCalcDiff(productRec);
  }, [subscribableProducts, subscribedProducts, reCalcDiff]);

  const isSubmitting = useRef(false);
  const handleAddSubmit = useCallback(async () => {
    const {
      diff,
      subscribedProducts: subscribed,
      minBorderPrice: minPrice,
      showConfirmMinPrice,
      updateSubscription,
    } = latest.current;
    if (isSubmitting.current || !subscribed || diff.size === 0) return;

    const { totalAmount, totalWeight } = calcTotalSummary(subscribed);
    const cart = calcCartSummary(diff);
    const totalPrice = totalAmount + cart.totalAmount;
    const isTimeReset = isYuPack(totalWeight) && !isYuPack(totalWeight - cart.totalWeight);

    const executeUpdate = async () => {
      isSubmitting.current = true;
      try {
        await updateSubscription(diff, isTimeReset);
      } finally {
        isSubmitting.current = false;
      }
    };

    if (minPrice < totalPrice && totalAmount < minPrice) {
      // 引数として渡す関数はアロー関数で包む
      showConfirmMinPrice(minPrice, async () => {
        await executeUpdate();
      });
    } else {
      await executeUpdate();
    }
  }, [latest]);

  useEffect(() => {
    const addSubmitMessageEvent = (e: MessageEvent) => {
      if (!e.data?.action) return;

      if (e.data.action === 'addSubmit') {
        handleAddSubmit();
      }
    };
    window.addEventListener('message', addSubmitMessageEvent);
    return () => window.removeEventListener('message', addSubmitMessageEvent);
  }, [handleAddSubmit]);

  useEffect(() => {
    if (!subscribedProducts) return;
    // 保存成功直後は resetDiff に伴う setTotal 再送を 1 回だけ止める。
    // この再送は旧小計で評価されることがあり、テーマ側の紙吹雪が誤発火するため。
    if (shouldSkipNextSetTotalPost.current) {
      shouldSkipNextSetTotalPost.current = false;
      return;
    }

    const total = calcTotalSummary(subscribedProducts);
    const cart = calcCartSummary(diff);

    const totalPrice = (total.totalAmount + cart.totalAmount).toLocaleString();
    const totalQuantity = total.totalQuantity + cart.totalQuantity;
    const hasDiff = diff.size > 0;

    window.parent.postMessage(
      {
        action: 'setTotal',
        totalPrice,
        totalQuantity,
        minPrice: minBorderPrice,
        maxPrice: maxBorderPrice,
        buttonDisplayFlag: hasDiff,
      },
      '*'
    );
  }, [diff, maxBorderPrice, minBorderPrice, subscribedProducts]);

  const hasProductData = Boolean(subscribedProducts && subscribableProducts);

  const onClickBoxUpdates = (
    items: SubscriptionProductItem[],
    quantities: Record<string, number>
  ) => {
    const productQuantities: QuantityUpdate[] = [];
    for (const item of items) {
      productQuantities.push({ product: item, newQty: quantities[item.id] });
    }
    changeQuantity(productQuantities);
    closeModal();
  };

  const subscribedHtml =
    subscribedProducts?.map((p) => (
    <ProductLayout
      productItem={p}
      column={1}
      diff={diff}
      changeQuantity={changeQuantity}
      onClickProductLink={handleClickProductLink}
      onClickBoxProductsSelect={onClickBoxProductsSelect}
      onClickBoxSetContents={onClickBoxSetContents}
    />
    )) ?? [];

  const subscribableHtml = subscribableProducts
    ? Object.keys(subscribableProducts.products).map((categoryName, index) => {
        const category = subscribableProducts.category[categoryName];
        const products = subscribableProducts.products[categoryName];

        return (
          <CategoryAccordion
            key={index}
            id={index}
            isOpen={isOpen[index]}
            category={category}
            onClickHandler={handleClickSwitchAccordion}
          >
            {isOpen[index] &&
              products.map((p) => {
                const column = category.column ?? 2;
                return (
                  <ProductLayout
                    key={p.type === 'product' ? p.product.id : p.box.parent.id}
                    productItem={p}
                    column={column}
                    diff={diff}
                    changeQuantity={changeQuantity}
                    onClickProductLink={handleClickProductLink}
                    onClickBoxProductsSelect={onClickBoxProductsSelect}
                    onClickBoxSetContents={onClickBoxSetContents}
                  />
                );
              })}
          </CategoryAccordion>
        );
      })
    : [];

  return (
    <>
      <div className={styles.nextDeliverySubscription}>
        <h2 className={styles.title}>次回お届け内容</h2>
        <div className={styles.productsContainer}>
          {displayState === 'ready' && hasProductData && (
            <>
              {subscribedHtml}
              <div className={styles.subscribableProductsHeader}>他の商品を追加する</div>
              {subscribableHtml}
            </>
          )}
          {displayState === 'loading' && (
            <div style={{ textAlign: 'center', padding: '24px 16px' }}>
              <img
                src={iconLoading}
                alt=""
                width={56}
                height={56}
              />
              <div className={styles.loadStateMessage} style={{ marginTop: '8px' }}>
                次回お届け内容を読み込んでいます。
              </div>
            </div>
          )}
          {displayState === 'stalled' && (
            <div style={{ padding: '20px 16px', textAlign: 'center' }}>
              <div className={styles.loadStateMessage} style={{ marginBottom: '12px' }}>
                読み込みに時間がかかっています。
              </div>
              <button
                className={backBtnClass.backBtn}
                type="button"
                onClick={onRetryLoad}
              >
                再読み込み
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ====== モーダル関連 ====== */}
      {productModal && productModal.kind === 'description' && (
        <ProductDescModal
          active={productModal !== null}
          closeFunc={closeModal}
          productDescModal={productModal.data}
        />
      )}

      {productModal && productModal.kind === 'boxUpdates' && (
        <BoxSelectProductsModal
          active={productModal !== null}
          closeFunc={closeModal}
          modal={productModal.data}
          onClickOk={onClickBoxUpdates}
        />
      )}
      {productModal && productModal.kind === 'boxSetContents' && (
        <BoxSetContentsModal
          active={productModal !== null}
          closeFunc={closeModal}
          modal={productModal.data}
        />
      )}
    </>
  );
}
